
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { addDays, addWeeks, format, parse, isValid, differenceInDays, differenceInWeeks } from 'date-fns';
import { Calendar, Baby } from 'lucide-react';
import { cn } from '@/lib/utils';

const PregnancyCalculator = () => {
  const [firstDay, setFirstDay] = useState('');
  const [currentDate, setCurrentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [calculationType, setCalculationType] = useState('lmp');
  const [results, setResults] = useState<{
    dueDate?: string;
    currentWeek?: number;
    currentDay?: number;
    trimester?: string;
    message?: string;
  }>({});

  const handleCalculate = () => {
    try {
      const firstDayDate = parse(firstDay, 'yyyy-MM-dd', new Date());
      const today = parse(currentDate, 'yyyy-MM-dd', new Date());

      if (!isValid(firstDayDate)) {
        setResults({ message: 'Please enter a valid date' });
        return;
      }

      let dueDate;
      
      if (calculationType === 'lmp') {
        // Due date = LMP + 280 days (40 weeks)
        dueDate = addDays(firstDayDate, 280);
      } else {
        // If conception date is known, add 266 days (38 weeks)
        dueDate = addDays(firstDayDate, 266);
      }

      // Calculate current week
      let startDate = calculationType === 'lmp' ? firstDayDate : addDays(firstDayDate, 14);
      const totalDays = differenceInDays(today, startDate);
      
      if (totalDays < 0) {
        setResults({ message: 'The start date cannot be in the future' });
        return;
      }
      
      const currentWeek = Math.floor(totalDays / 7) + 1;
      const currentDay = (totalDays % 7) + 1;

      // Determine trimester
      let trimester = '';
      if (currentWeek <= 13) {
        trimester = 'First Trimester';
      } else if (currentWeek <= 26) {
        trimester = 'Second Trimester';
      } else if (currentWeek <= 40) {
        trimester = 'Third Trimester';
      } else {
        trimester = 'Past Due Date';
      }

      setResults({
        dueDate: format(dueDate, 'MMMM d, yyyy'),
        currentWeek: currentWeek > 42 ? 42 : currentWeek,
        currentDay: currentDay,
        trimester: trimester
      });
    } catch (error) {
      setResults({ message: 'Error calculating pregnancy dates' });
    }
  };

  const getTrimesterClass = (trimester: string | undefined) => {
    if (!trimester) return '';
    if (trimester === 'First Trimester') return 'bg-blue-100 text-blue-800';
    if (trimester === 'Second Trimester') return 'bg-green-100 text-green-800';
    if (trimester === 'Third Trimester') return 'bg-purple-100 text-purple-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Pregnancy Calculator</h3>
            <p className="text-sm text-muted-foreground">
              Calculate your due date and track your pregnancy progress.
            </p>
          </div>
          
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="calculationType">Calculation Method</Label>
              <Select 
                value={calculationType} 
                onValueChange={setCalculationType}
              >
                <SelectTrigger className="focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none">
                  <SelectValue placeholder="Select calculation method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lmp">Last Menstrual Period (LMP)</SelectItem>
                  <SelectItem value="conception">Conception Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="firstDay">
                {calculationType === 'lmp' ? 'First Day of Last Period' : 'Conception Date'}
              </Label>
              <div className="flex">
                <Input
                  id="firstDay"
                  type="date"
                  value={firstDay}
                  onChange={(e) => setFirstDay(e.target.value)}
                  className="flex-1 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none"
                />
                <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                  <Calendar className="h-4 w-4" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {calculationType === 'lmp' 
                  ? 'Enter the first day of your last menstrual period' 
                  : 'Enter the estimated date of conception'}
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="currentDate">Today's Date</Label>
              <div className="flex">
                <Input
                  id="currentDate"
                  type="date"
                  value={currentDate}
                  onChange={(e) => setCurrentDate(e.target.value)}
                  className="flex-1 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none"
                />
                <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                  <Calendar className="h-4 w-4" />
                </div>
              </div>
            </div>
            
            <Button onClick={handleCalculate} className="mt-2 focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:outline-none">
              Calculate
            </Button>
          </div>

          {results.message ? (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {results.message}
            </div>
          ) : results.dueDate ? (
            <div className="mt-6 space-y-4">
              <div className="p-4 border rounded-md">
                <div className="flex items-center mb-4">
                  <Baby className="h-6 w-6 mr-2 text-primary" />
                  <h4 className="text-lg font-medium">Pregnancy Details</h4>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium">Estimated Due Date:</p>
                    <p className="text-lg font-semibold text-primary">{results.dueDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Current Progress:</p>
                    <p>
                      Week {results.currentWeek}, Day {results.currentDay}
                      <span className={cn("ml-2 px-2 py-0.5 text-xs rounded-full", getTrimesterClass(results.trimester))}>
                        {results.trimester}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-primary h-2.5 rounded-full" 
                    style={{ width: `${Math.min(((results.currentWeek || 0) / 40) * 100, 100)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-600">
                  <span>First Trimester</span>
                  <span>Second Trimester</span>
                  <span>Third Trimester</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default PregnancyCalculator;
